import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useMatchmaking } from "@/hooks/useMatchmaking";
import { useAuth } from "@/hooks/useAuth";
import { MatchmakingDialog } from "@/components/MatchmakingDialog";
import { 
  Zap, 
  Globe, 
  Clock, 
  Users, 
  Trophy,
  Target,
  TrendingUp,
  Bot,
  Loader2,
  AlertCircle
} from "lucide-react";

const timeControls = [
  { label: "Bullet", value: "1+0", icon: Zap, description: "Lightning fast!" },
  { label: "Blitz", value: "3+0", icon: Clock, description: "Quick thinking" },
  { label: "Blitz", value: "5+0", icon: Trophy, description: "Most popular" },
  { label: "Rapid", value: "10+0", icon: Target, description: "Balanced pace" },
  { label: "Rapid", value: "15+10", icon: TrendingUp, description: "Strategic" },
  { label: "Classic", value: "30+0", icon: Globe, description: "Deep thought" },
];

export default function PlayOnline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    userProfile,
    isInQueue,
    isSearching,
    matchFound,
    queueStats,
    joinQueue,
    leaveQueue,
    offerBotMatch,
    fetchUserProfile
  } = useMatchmaking();

  const [selectedTimeControl, setSelectedTimeControl] = useState("5+0");
  const [customEloTolerance, setCustomEloTolerance] = useState(100);
  const [showMatchmaking, setShowMatchmaking] = useState(false);

  // Fetch user profile on mount
  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  // Handle Quick Play
  const handleQuickPlay = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    
    setShowMatchmaking(true);
    await joinQueue("5+0");
  };

  // Handle Find Match with custom settings
  const handleFindMatch = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    
    setShowMatchmaking(true);
    await joinQueue(selectedTimeControl);
  };

  // Handle match found
  const handleMatchFound = (
    gameSessionId: string,
    timeControl: string,
    playerColor: 'white' | 'black',
    isBot?: boolean,
    botElo?: number
  ) => {
    setShowMatchmaking(false);
    navigate(`/?game=${gameSessionId}&mode=direct`);
  };

  if (!user) {
    return (
      <ChessAppLayout>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Alert className="border-accent">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please <Button variant="link" className="p-0 h-auto" onClick={() => navigate("/auth")}>sign in</Button> to play online matches.
            </AlertDescription>
          </Alert>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="container mx-auto px-4 py-8 max-w-5xl space-y-8">
        {/* Quick Play Hero Section */}
        <Card className="border-accent bg-gradient-to-br from-accent/20 via-accent/10 to-transparent">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center gap-2 mb-2 justify-center md:justify-start">
                  <Zap className="w-6 h-6 text-accent" />
                  <h2 className="text-2xl font-bold text-accent">Quick Play</h2>
                </div>
                <p className="text-muted-foreground">
                  Find an opponent instantly! Matches you with a player ±100 ELO in 5+0 Blitz
                </p>
              </div>
              <Button 
                size="lg" 
                onClick={handleQuickPlay}
                disabled={isSearching || isInQueue}
                className="bg-accent hover:bg-accent/90 text-accent-foreground min-w-[200px]"
              >
                {isSearching || isInQueue ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Zap className="mr-2 h-5 w-5" />
                    Quick Play
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* User Profile Card */}
        {userProfile && (
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 text-center md:text-left">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-accent/20 rounded-full">
                    <Users className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{userProfile.display_name || "Player"}</p>
                    <p className="text-sm text-muted-foreground">Ready to play</p>
                  </div>
                </div>
                
                <div className="flex gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-accent">{userProfile.elo_rating}</p>
                    <p className="text-xs text-muted-foreground">ELO Rating</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-500">{userProfile.games_won}</p>
                    <p className="text-xs text-muted-foreground">Wins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{userProfile.games_lost}</p>
                    <p className="text-xs text-muted-foreground">Losses</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-500">{userProfile.games_drawn}</p>
                    <p className="text-xs text-muted-foreground">Draws</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Time Control Selection */}
        <div>
          <h3 className="text-xl font-semibold mb-4 text-center">Select Time Control</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {timeControls.map((control) => (
              <Card
                key={control.value}
                className={`cursor-pointer transition-all duration-200 hover:scale-105 ${
                  selectedTimeControl === control.value
                    ? "border-accent bg-accent/10"
                    : "border-border hover:border-accent/50"
                }`}
                onClick={() => setSelectedTimeControl(control.value)}
              >
                <CardContent className="p-4 text-center">
                  <control.icon className={`w-6 h-6 mx-auto mb-2 ${
                    selectedTimeControl === control.value ? "text-accent" : "text-muted-foreground"
                  }`} />
                  <p className="font-semibold text-sm mb-1">{control.value}</p>
                  <Badge variant="outline" className="text-xs">{control.label}</Badge>
                  <p className="text-xs text-muted-foreground mt-1">{control.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Matchmaking Options */}
        <Card>
          <CardContent className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Matchmaking Options</h3>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>ELO Range: ±{customEloTolerance}</Label>
                    <Badge variant="secondary">{customEloTolerance === 100 ? "Recommended" : "Custom"}</Badge>
                  </div>
                  <Slider
                    value={[customEloTolerance]}
                    onValueChange={(value) => setCustomEloTolerance(value[0])}
                    min={50}
                    max={400}
                    step={50}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Range automatically expands if no match is found
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Queue Statistics */}
        {queueStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-accent" />
                <p className="text-2xl font-bold">{queueStats.total_waiting}</p>
                <p className="text-sm text-muted-foreground">Players in queue</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-accent" />
                <p className="text-2xl font-bold">~{queueStats.avg_wait_seconds}s</p>
                <p className="text-sm text-muted-foreground">Average wait time</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <Trophy className="w-8 h-8 mx-auto mb-2 text-accent" />
                <p className="text-2xl font-bold">{queueStats.matches_last_hour}</p>
                <p className="text-sm text-muted-foreground">Matches last hour</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Find Match Button */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={handleFindMatch}
            disabled={isSearching || isInQueue}
            className="min-w-[300px]"
          >
            {isSearching || isInQueue ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Searching for opponent...
              </>
            ) : (
              <>
                <Globe className="mr-2 h-5 w-5" />
                Find Opponent
              </>
            )}
          </Button>
        </div>

        {/* AI Squad Alternative */}
        <Card className="border-accent/50">
          <CardContent className="p-6 text-center">
            <Bot className="w-10 h-10 mx-auto mb-3 text-accent" />
            <h3 className="text-lg font-semibold mb-2">Prefer AI Opponents?</h3>
            <p className="text-muted-foreground mb-4">
              Play against our smart AI bots at any skill level
            </p>
            <Button variant="outline" onClick={() => navigate("/?show=ai-squad")}>
              Challenge AI Squad
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Matchmaking Dialog */}
      <MatchmakingDialog
        open={showMatchmaking}
        onOpenChange={setShowMatchmaking}
        onMatchFound={handleMatchFound}
      />
    </ChessAppLayout>
  );
}
